import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import stSeat from '../../../assets/images/icons/st_seat.svg'
import vipSeat from '../../../assets/images/icons/vip_seat.svg'
import backgroundImage from '../../../assets/images/boat_background.svg'
import { columns } from './seatLayoutUtils'

/**
 * Standard Seat Layout Component
 * Handles standard vessel layout with VIP and STD sections
 */
export function StandardSeatLayout({
    seatLayout,
    selectedSeats,
    selectedPassengerIndx,
    tripDetails,
    currentTripType,
    departureSeatLayoutKey,
    returnSeatLayoutKey,
    seatLayoutKey,
    setIsConfirmEnabled,
    setPreviewSeat,
}) {
    const { t } = useTranslation()

    /**
     * Render individual seat (VIP or STD)
     */
    const renderSeat = (seat, seatType) => {
        const seatKey = `${seat.row}${seat.column}`
        const isCurrentPassengerSeat = selectedSeats[seatKey] === selectedPassengerIndx
        const isAvailable = seat.available && !selectedSeats[seatKey]

        return (
            <div className="flex justify-center items-center">
                <label className="relative">
                    <input
                        type="checkbox"
                        className={`
                            appearance-none h-[30px] w-[30px] border-2 rounded-md cursor-pointer 
                            flex items-center justify-center text-lg font-semibold
                            ${isAvailable
                                ? 'bg-white border-[1px] border-[#e5e5e5] shadow-sm'
                                : seat.available 
                                    ? 'bg-[#E0E8E9] border-[0px]'
                                    : 'bg-[#E0E8E9] border-[0px]'
                            }
                        `}
                        disabled={
                            !seat.available ||
                            (selectedSeats[seatKey] &&
                                selectedSeats[seatKey] !== selectedPassengerIndx)
                        }
                        checked={isCurrentPassengerSeat}
                        onChange={() => {
                            setIsConfirmEnabled(true)
                            setPreviewSeat({
                                colIndex: columns.indexOf(seat.column),
                                seatRow: seat.row,
                                seatType: seatType,
                                seatNumber: seatKey,
                            })
                        }}
                    />
                    {/* Seat Number inside Checkbox */}
                    <span
                        className={`absolute inset-0 flex justify-center items-center text-sm font-bold rounded-md p-[15px] w-[15px] h-[15px] m-auto
                            ${isCurrentPassengerSeat
                                ? 'text-white bg-primary'
                                : ''
                            }
                            ${!selectedSeats[seatKey] && seat.available
                                ? 'text-gray-700'
                                : 'text-gray-400'
                            }
                        `}
                    >
                        {seat.row}
                    </span>
                </label>
            </div>
        )
    }

    // Determine which layout key to use
    const currentLayoutKey = tripDetails.isRoundTrip
        ? (currentTripType === 'departure' ? departureSeatLayoutKey : returnSeatLayoutKey)
        : seatLayoutKey

    // Check if VIP section should be shown
    const showVIPSection = ((tripDetails.isRoundTrip && currentTripType === 'departure' && departureSeatLayoutKey === 'standard_vessels') ||
                            (tripDetails.isRoundTrip && currentTripType === 'return' && returnSeatLayoutKey === 'standard_vessels') ||
                            (!tripDetails.isRoundTrip && seatLayoutKey === 'standard_vessels')) && seatLayout.VIP.length > 0

    return (
        <div className="w-full relative h-auto">
            <div
                className="relative border-solid border-[#e0e8e9] md:pt-[40%] xl:pt-[45%]"
                style={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'bottom',
                    paddingInlineStart: '20px',
                    paddingInlineEnd: '20px',
                    paddingBottom: '10px',
                }}
            >
                {/* VIP Seats Section - Only show for standard_vessels layout */}
                {showVIPSection && (
                    <>
                        <div className="flex space-x-5 justify-center w-full px-4">
                            {/* VIP Seats Label */}
                            <div className="flex w-full text-start text-[14px] font-semibold text-primary mb-2 ps-3 border-b-[1px] border-solid border-gray-200 pb-2">
                                <img
                                    src={vipSeat}
                                    className="w-[20px] me-2"
                                    alt="VIP"
                                />
                                {t('VIP Seats')}
                            </div>
                        </div>
                        <div className="flex space-x-5 justify-center w-full px-4">
                            {columns.map((column, colIndex) => (
                                <div className="flex-1" key={colIndex}>
                                    {/* Column Label */}
                                    <div className="text-center font-semibold text-primary mb-2">
                                        {column}
                                    </div>

                                    {/* VIP Seats */}
                                    <div className="space-y-2">
                                        {seatLayout.VIP.filter(
                                            seat => seat.column === column
                                        ).map((seat, index) => (
                                            <div key={index}>
                                                {renderSeat(seat, 'VIP')}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}  
                        </div>
                    </>
                )}

                {/* STD Seats Section */}
                <div className="flex space-x-5 justify-center w-full mt-4 px-4">
                    {/* STD Seats Label */}
                    <div className="flex w-full text-start text-[14px] font-semibold text-primary mb-2 ps-3 border-b-[1px] border-solid border-gray-200 pb-2">
                        <img
                            src={stSeat}
                            className="w-[20px] me-2"
                            alt="STD"
                        />
                        {t('Standard Seats')}
                    </div>
                </div>
                <div className="flex space-x-5 justify-center w-full px-4">
                    {columns.map((column, colIndex) => (
                        <div className="flex-1" key={colIndex}>
                            {/* Column Label */}
                            <div className="text-center font-semibold text-primary mb-2">
                                {column}
                            </div>

                            {/* STD Seats */}
                            <div className="space-y-2">
                                {seatLayout.STD.filter(
                                    seat => seat.column === column
                                ).map((seat, index) => (
                                    <div key={index}>
                                        {renderSeat(seat, 'STD')}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

StandardSeatLayout.propTypes = {
    seatLayout: PropTypes.object.isRequired,
    selectedSeats: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    selectedPassengerIndx: PropTypes.number.isRequired,
    tripDetails: PropTypes.object.isRequired,
    currentTripType: PropTypes.string,
    departureSeatLayoutKey: PropTypes.string,
    returnSeatLayoutKey: PropTypes.string,
    seatLayoutKey: PropTypes.string,
    setIsConfirmEnabled: PropTypes.func.isRequired,
    setPreviewSeat: PropTypes.func.isRequired,
}
